/**
 * Shared enums and constant tuples. Imported by both client and server so they
 * can never disagree on the spelling of a status, stat, or kingdom slug.
 */

export const BATTLE_STATS = ['strength', 'defense', 'speed', 'dexterity'] as const;
export type BattleStat = (typeof BATTLE_STATS)[number];

export const WORKING_STATS = ['toil', 'wits', 'endurance'] as const;
export type WorkingStat = (typeof WORKING_STATS)[number];

export const WORKING_STAT_LABELS: Record<WorkingStat, string> = {
  toil: 'Toil',
  wits: 'Wits',
  endurance: 'Endurance',
};

export const RESOURCES = ['vigor', 'resolve', 'spirit', 'life'] as const;
export type ResourceName = (typeof RESOURCES)[number];

/** Display labels for resources — shown in the header bar in this order. */
export const RESOURCE_LABELS: Record<ResourceName, string> = {
  vigor: 'Vigor',
  resolve: 'Resolve',
  spirit: 'Spirit',
  life: 'Life',
};

export type CharacterStatus = 'OKAY' | 'TRAVELLING' | 'INFIRMARY' | 'DUNGEON';

export const STATUS_LABELS: Record<CharacterStatus, string> = {
  OKAY: 'Okay',
  TRAVELLING: 'On the road',
  INFIRMARY: 'In the Infirmary',
  DUNGEON: 'In the Dungeon',
};

export type AccountRole = 'PLAYER' | 'MODERATOR' | 'ADMIN';

/** Home kingdom slug — Rex Mortis itself. */
export const HOME_KINGDOM = 'rex_mortis';

// ──────────────────────────────────────────────────────────────────────────────
// CRIMES
// ──────────────────────────────────────────────────────────────────────────────

export type CrimeOutcome = 'TRIUMPH' | 'PARTIAL' | 'FOILED' | 'DISASTROUS';

export const CRIME_OUTCOME_LABELS: Record<CrimeOutcome, string> = {
  TRIUMPH: 'Triumph',
  PARTIAL: 'Partial',
  FOILED: 'Foiled',
  DISASTROUS: 'Disastrous',
};

export type CrimeCategory =
  | 'banditry'
  | 'smuggling'
  | 'forgery'
  | 'poisoning'
  | 'conspiracy'
  | 'brigandage'
  | 'high_schemes';

export const CRIME_CATEGORY_LABELS: Record<CrimeCategory, string> = {
  banditry: 'Banditry',
  smuggling: 'Smuggling',
  forgery: 'Forgery',
  poisoning: 'Poisoning',
  conspiracy: 'Conspiracy',
  brigandage: 'Brigandage',
  high_schemes: 'High Schemes',
};

// ──────────────────────────────────────────────────────────────────────────────
// GOOD DEEDS — the lawful mirror of crimes. No DISASTROUS band (the honourable
// are never jailed for trying); a failed deed is simply FOILED.
// ──────────────────────────────────────────────────────────────────────────────

export type GoodDeedOutcome = 'TRIUMPH' | 'PARTIAL' | 'FOILED';

export const GOOD_DEED_OUTCOME_LABELS: Record<GoodDeedOutcome, string> = {
  TRIUMPH: 'Triumph',
  PARTIAL: 'Partial',
  FOILED: 'Unfulfilled',
};

export type GoodDeedCategory =
  | 'alms'
  | 'relief'
  | 'stewardship'
  | 'healing'
  | 'diplomacy'
  | 'protection'
  | 'great_undertakings';

export const GOOD_DEED_CATEGORY_LABELS: Record<GoodDeedCategory, string> = {
  alms: 'Almsgiving',
  relief: 'Relief',
  stewardship: 'Stewardship',
  healing: 'Healing',
  diplomacy: 'Diplomacy',
  protection: 'Protection',
  great_undertakings: 'Great Undertakings',
};

// ──────────────────────────────────────────────────────────────────────────────
// ITEMS
// ──────────────────────────────────────────────────────────────────────────────

export type ItemCategory =
  | 'melee'
  | 'ranged'
  | 'armor_chest'
  | 'armor_head'
  | 'armor_shield'
  | 'elixir'
  | 'tonic'
  | 'material'
  | 'amulet'
  | 'tool'
  | 'food'
  | 'chest'
  | 'misc';

export const ITEM_CATEGORY_LABELS: Record<ItemCategory, string> = {
  melee: 'Melee Weapons',
  ranged: 'Ranged Weapons',
  armor_chest: 'Armour',
  armor_head: 'Helms',
  armor_shield: 'Shields',
  amulet: 'Amulets',
  tool: 'Tools',
  elixir: 'Elixirs',
  tonic: 'Tonics',
  food: 'Provisions',
  material: 'Materials',
  chest: 'Coin Chests',
  misc: 'Curios',
};

export type ItemTier =
  | 'crude'
  | 'common'
  | 'fine'
  | 'masterwork'
  | 'legendary'
  | 'artifact'
  | 'mythic';

export const ITEM_TIER_LABELS: Record<ItemTier, string> = {
  crude: 'Crude',
  common: 'Common',
  fine: 'Fine',
  masterwork: 'Masterwork',
  legendary: 'Legendary',
  artifact: 'Artifact',
  mythic: 'Mythic',
};
